import React from 'react';
import { motion } from 'framer-motion';
import { Zap, Target, Trophy, Users, Code, Globe, Monitor } from 'lucide-react';
import { useDeviceDetection } from '../hooks/useDeviceDetection';

const About: React.FC = () => {
  const { isDesktop } = useDeviceDetection();
  const features = [
    {
      icon: Zap,
      title: 'Lightning Fast',
      description: 'Real-time performance tracking with instant feedback on your typing speed and accuracy.'
    },
    {
      icon: Target,
      title: 'Precision Focused',
      description: 'Industry-standard WPM calculations that match professional typing benchmarks.'
    },
    {
      icon: Trophy,
      title: 'Competitive Gaming',
      description: 'Challenge friends in real-time battles or climb the ranked ladder for glory.'
    },
    {
      icon: Users,
      title: 'Global Community',
      description: 'Join thousands of typists worldwide improving their skills every day.'
    },
    {
      icon: Code,
      title: 'Code Practice',
      description: 'Specialized modes for developers to practice typing code in multiple languages.'
    },
    {
      icon: Globe,
      title: 'Desktop Optimized',
      description: 'Designed specifically for desktop users with physical keyboards for the best typing practice experience.'
    }
  ];

  return (
    <div className="max-w-5xl mx-auto">
      {/* Desktop-Only Notice for Mobile Users */}
      {!isDesktop && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-accent/20 border-2 border-accent rounded-xl p-6 mb-8 text-center"
        >
          <div className="flex justify-center mb-3">
            <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center">
              <Monitor className="w-6 h-6 text-primary" />
            </div>
          </div>
          <h3 className="text-xl font-bold text-accent mb-2">Desktop Required</h3>
          <p className="text-textMuted">
            TypeRush is optimized for desktop and laptop computers only. 
            Please visit us from a desktop device to access the full typing experience.
          </p>
        </motion.div>
      )}
      
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-16"
      >
        <h1 className="text-5xl md:text-6xl font-bold mb-6">
          About <span className="text-accent">TypeRush</span>
        </h1>
        <p className="text-xl text-textMuted max-w-3xl mx-auto leading-relaxed">
          TypeRush is more than just a typing test platform. We're building the future of 
          typing education through gamification, competition, and precision measurement.
        </p>
      </motion.div>

      {/* Mission Statement */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="bg-cardBg rounded-xl p-8 md:p-12 mb-12 border border-secondary"
      >
        <h2 className="text-3xl font-bold mb-4 text-accent">Our Mission</h2>
        <p className="text-textMuted text-lg leading-relaxed mb-4">
          We believe that typing is the fundamental skill of the digital age. Every keystroke 
          matters in today's fast-paced world, and we're committed to helping everyone from 
          students to professionals master this essential skill.
        </p>
        <p className="text-textMuted text-lg leading-relaxed">
          TypeRush combines cutting-edge technology with engaging gameplay to create an 
          environment where improvement is both measurable and fun. Our platform uses the 
          same accuracy standards as industry leaders, ensuring your progress translates 
          to real-world benefits.
        </p>
      </motion.div>

      {/* Features Grid */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="mb-16"
      >
        <h2 className="text-3xl font-bold mb-8 text-center">What Makes Us Different</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                className="bg-cardBg rounded-lg p-6 border border-secondary hover:border-accent/50 transition-colors"
              >
                <Icon className="w-10 h-10 text-accent mb-4" />
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-textMuted">{feature.description}</p>
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      {/* Stats Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.8 }}
        className="bg-gradient-to-r from-accent/10 to-accent/5 rounded-xl p-8 md:p-12 mb-12 border border-accent/20"
      >
        <h2 className="text-3xl font-bold mb-8 text-center">TypeRush by the Numbers</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="text-center">
            <div className="text-4xl font-bold text-accent mb-2">100K+</div>
            <div className="text-textMuted">Active Users</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-accent mb-2">10M+</div>
            <div className="text-textMuted">Tests Completed</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-accent mb-2">50K+</div>
            <div className="text-textMuted">Daily Battles</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-accent mb-2">95%</div>
            <div className="text-textMuted">User Satisfaction</div>
          </div>
        </div>
      </motion.div>

      {/* Team Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1 }}
        className="text-center mb-12"
      >
        <h2 className="text-3xl font-bold mb-4">Built by Typing Enthusiasts</h2>
        <p className="text-textMuted text-lg max-w-3xl mx-auto leading-relaxed">
          Our team consists of developers, designers, and competitive typists who understand 
          the importance of every millisecond and every keystroke. We're constantly iterating 
          based on community feedback to create the best typing experience possible.
        </p>
      </motion.div>

      {/* Contact CTA */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.2 }}
        className="bg-cardBg rounded-xl p-8 text-center border border-secondary"
      >
        <h2 className="text-2xl font-bold mb-4">Join the TypeRush Revolution</h2>
        <p className="text-textMuted mb-6">
          Ready to take your typing skills to the next level?
        </p>
        <button
          onClick={() => globalThis.location.href = '/'}
          className="bg-accent hover:bg-yellow-500 text-primary font-semibold px-8 py-3 rounded-lg transition-all duration-200 transform hover:scale-105"
        >
          Start Typing Now
        </button>
      </motion.div>
    </div>
  );
};

export default About;
